"use client";
import { faFacebookSquare, faLinkedin, faXTwitter, faYoutube } from "@awesome.me/kit-18b0dd684a/icons/classic/brands";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image, { StaticImageData } from "next/image";
import Link from "next/link";
import G2Top100Img from "~/public/assets/img/g2-badges/top100-highest-satisfaction.svg";
import G2Top50Img from "~/public/assets/img/g2-badges/top50-content-management.svg";
import googleForStartupsImg from "~/public/assets/img/google-for-startups.png";
import iconBotImg from "~/public/assets/img/icon-bot-new.svg";
import navData from "~/public/home/navData.json";
import Logo from "../../components/Front/Logo";
import { ColumnType, MenuBlockType, NavDataType, SubmenuType } from "../Header/HomepageHeader";

const HomepageFooter = () => {
  const currentYear = new Date().getFullYear();

  const footerNavData: NavDataType = [
    ...navData
      .map((item, index) =>
        index === navData.length - 1
          ? {
              ...item,
              columns: item.columns.map((column) => ({
                ...column,
                submenu: [
                  ...(column.submenu || []),
                  {
                    title: "Company",
                    content: [
                      { title: "Contact", link: "https://visualping.io/contact" },
                      { title: "Careers", link: "https://visualping.io/careers" },
                      { title: "Press", link: "https://visualping.io/press" },
                      { title: "Blog", link: "https://visualping.io/blog" },
                    ],
                  },
                ],
              })),
            }
          : item
      )
      .sort((a, b) => {
        if (a?.order === undefined) return 1;
        if (b?.order === undefined) return -1;
        return a.order - b.order;
      }),
  ];

  const imageColumns = [
    {
      title: "Part of",
      columns: [{ backgroundImage: [googleForStartupsImg], bgImgClassname: "w-32" }],
    },
    {
      title: "voted by our users",
      columns: [{ backgroundImage: [G2Top100Img, G2Top50Img], bgImgClassname: "w-24" }],
    },
  ];

  return (
    <footer className="bg-vpDarkBlue-600 px-10 py-8 text-white md:py-24">
      <div className="mx-auto max-w-7xl space-y-8 md:space-y-16">
        <div className="md:border-b-1 flex flex-col items-center justify-between border-b-0 border-vpGray-800 md:flex-row md:pb-16">
          <div className="mb-4 md:mb-0">
            <p className="mb-4 text-center text-2xl font-semibold leading-[3.75rem] md:text-left md:text-5xl">
              Stay in the know
            </p>
            <p className="text-center text-lg text-vpGray-700 md:text-left md:text-xl">
              Save time and get notified of the important changes your team needs.
            </p>
          </div>
          <SignupButton />
        </div>
        <div className="flex w-full flex-col justify-between md:flex-row">
          <div className="flex flex-col md:flex-row">
            {footerNavData.map((item, index) => (
              <FooterColumns item={item} key={index} />
            ))}
          </div>
          <div className="hidden flex-col gap-10 md:flex md:flex-row">
            {imageColumns.map((item, index) => (
              <FooterColumns item={item} key={index} />
            ))}
          </div>
        </div>

        <div className="flex md:justify-between">
          <div className="hidden md:block">
            <Link href="https://visualping.io/" aria-label="Visualping">
              <Logo white />
            </Link>
            <p className="mt-2">Webmonitoring Technologies Inc.</p>
            <p className="mt-3 text-sm text-vpGray-600">595 Burrard Street, 16th floor Vancouver BC, V7X 1L3 Canada</p>
          </div>
          <div className="w-full md:w-auto">
            <div className="hidden items-center gap-4 pb-5 md:flex">
              <Link
                href="https://twitter.com/visualping"
                className="hover:opacity-50"
                rel="noopener noreferrer"
                target="_blank"
                aria-label="X (Twitter)"
              >
                <FontAwesomeIcon className="fa-xl text-white" icon={faXTwitter} />
              </Link>
              <Link
                href="https://www.linkedin.com/company/9480446"
                className="hover:opacity-50"
                rel="noopener noreferrer"
                target="_blank"
                aria-label="LinkedIn"
              >
                <FontAwesomeIcon className="fa-xl text-white" icon={faLinkedin} />
              </Link>
              <Link
                href="https://www.youtube.com/channel/UCgyjvEr5OWQ10DR82_HWgjg"
                className="hover:opacity-50"
                rel="noopener noreferrer"
                target="_blank"
                aria-label="Youtube"
              >
                <FontAwesomeIcon className="fa-xl text-white" icon={faYoutube} />
              </Link>
              <Link
                href="https://www.facebook.com/visualping.io"
                className="hover:opacity-50"
                rel="noopener noreferrer"
                target="_blank"
                aria-label="Facebook"
              >
                <FontAwesomeIcon className="fa-xl text-white" icon={faFacebookSquare} />
              </Link>
            </div>

            <p className="text-sm text-vpGray-300 md:text-vpGray-600">©{currentYear} Webmonitoring Technologies Inc.</p>
            <div className="flex flex-col items-center space-y-4 text-sm md:flex-row md:space-x-4 md:space-y-0">
              <Link href="https://visualping.io/privacy" className="text-sm text-vpGray-300 md:text-vpGray-600">
                Privacy policy
              </Link>
              <Link href="https://visualping.io/terms" className="text-vpGray-300 md:text-vpGray-600">
                Terms of use
              </Link>
            </div>
          </div>
        </div>
        <div className="mx-auto flex w-full flex-col text-xs text-vpGray-700 md:text-vpGray-600 lg:flex-row">
          <Image
            src={iconBotImg}
            width={iconBotImg.width}
            height={iconBotImg.height}
            alt="Good bot disclaimer"
            className="item-center mx-auto mb-4 w-10 lg:mb-0 lg:mr-4"
          />
          <p>
            <span className="font-bold text-vpGray-800 md:text-vpGray-600">Good bot disclaimer</span> Like other good
            bots such as Google, Alexa and others, Visualping abides to current international guidelines on accessing
            websites on a respectful, non-intrusive and ethical manner. Our bots, for example, only crawl pages at a low
            frequency to protect site&apos;s bandwidth. Our system will also never attempt to solve CAPTCHAS or to
            circumvent systems designed to protect sites from unwanted crawling. Our service also stores publicly
            available information from websites respecting copyright laws and purely for the purpose to replace an end
            user wishing to be notified when a change occurs. Visualping will never package and resell data or crawl
            sites to train A.I models.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default HomepageFooter;

const ColumnWithBackground = ({ column, itemTitle }: { column: ColumnType; itemTitle: string }) => (
  <>
    {Array.isArray(column.backgroundImage) && (
      <div className="space-y-3 py-4">
        <div className="pr-6 text-sm font-bold uppercase">{itemTitle}</div>
        <div className="flex gap-8">
          {column.backgroundImage.map((image: StaticImageData) => (
            <Image
              key={image.src}
              src={image.src}
              width={image.width}
              height={image.height}
              className={column.bgImgClassname}
              alt={itemTitle}
            />
          ))}
        </div>
      </div>
    )}
  </>
);

const SubmenuContent = ({ submenu, itemTitle }: { submenu: SubmenuType; itemTitle: string }) => {
  return (
    <div className="space-y-3 py-4 text-center md:pr-6 md:text-left">
      <div className="font-bold uppercase md:text-sm">{submenu.title || itemTitle}</div>
      <div className="flex flex-col space-y-3 lg:space-y-1">
        {submenu.content.map((contentItem) => (
          <Link
            key={contentItem.title}
            href={contentItem.link ?? "#"}
            target={contentItem.link.startsWith("#") ? "" : "_blank"}
            className="justify-center text-vpGray-300 transition-colors hover:text-vpGray-500 md:justify-start md:text-vpGray-600"
            onClick={() => {}}
          >
            {contentItem.title}
          </Link>
        ))}
      </div>
    </div>
  );
};

const FooterColumns = ({ item }: { item: MenuBlockType }) => {
  const renderColumns = item.columns.map((column: ColumnType, index: number) => (
    <div key={index}>
      <ColumnWithBackground column={column} itemTitle={item.title} />
      {column.submenu?.map((submenu: SubmenuType, submenuIndex: number) => (
        <SubmenuContent key={submenuIndex} submenu={submenu} itemTitle={item.title} />
      ))}
    </div>
  ));

  return item?.splitColumn === false ? <div>{renderColumns}</div> : <>{renderColumns}</>;
};

const SignupButton = () => {
  return (
    <Link
      href="https://visualping.io/sign-up"
      className="w-full justify-center rounded-lg bg-vpBlue-500 px-6 py-4 text-center text-white transition hover:bg-vpBlue-700 hover:text-white md:w-auto md:py-3"
      onClick={() => {}}
    >
      <span className="mr-1 font-semibold">Get started</span>
      <span>- it&apos;s free</span>
    </Link>
  );
};
